import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Airplane, Camera, MapPin, Clock, SignOut, CheckCircle, WarningCircle, ArrowsClockwise, Radio } from "@phosphor-icons/react";

export default function MobilePunch() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // If we just came back from /walkie, skip the auto-jump so the worker can
  // reach the CLOCK OUT button. Only true for one render — we clear it below.
  const cameFromWalkie = !!location.state?.fromWalkie;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState("");
  const [busy, setBusy] = useState(null); // "in" | "out"
  const [recent, setRecent] = useState([]);
  const [now, setNow] = useState(new Date());
  const [facingMode, setFacingMode] = useState("user");
  const [flipping, setFlipping] = useState(false);
  const [initialChecked, setInitialChecked] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } }, audio: false,
        });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        if (!cancelled) {
          if (facingMode === "environment") {
            toast.error("Back camera not available, using front camera");
            setFacingMode("user");
          } else {
            toast.error("Camera permission required to punch");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [facingMode]);

  const flipCamera = async () => {
    if (flipping) return;
    setFlipping(true);
    setFacingMode((m) => (m === "user" ? "environment" : "user"));
    setTimeout(() => setFlipping(false), 600);
  };

  useEffect(() => {
    if (!navigator.geolocation) { setGpsErr("GPS not available"); return; }
    const w = navigator.geolocation.watchPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }); setGpsErr(""); },
      (e) => setGpsErr(e.message || "GPS error"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(w);
  }, []);

  const load = async (autoJumpToWalkie = false) => {
    try {
      const { data } = await api.get("/punch/my-punches", { params: { limit: 10 } });
      setRecent(data);
      const latest = data && data[0];
      if (autoJumpToWalkie && latest && latest.punch_type === "in") {
        navigate("/walkie", { replace: true });
      }
    } catch (e) { /* silent */ }
    finally { setInitialChecked(true); }
  };

  // On first load, if latest punch is IN, redirect to walkie-talkie screen
  // automatically — UNLESS the worker just came back from /walkie to clock out.
  useEffect(() => {
    load(!cameFromWalkie);
    // Clear the state flag so a later refresh will auto-jump again.
    if (cameFromWalkie) {
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line
  }, []);

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "punch_worker") return <Navigate to="/dashboard" replace />;

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    return c.toDataURL("image/jpeg", 0.7);
  };

  const punch = async (type) => {
    if (!gps) { toast.error("Waiting for GPS…"); return; }
    setBusy(type);
    const photo_base64 = takePhoto();
    if (!photo_base64) { toast.error("Camera not ready"); setBusy(null); return; }
    try {
      await api.post("/punch/punches", {
        punch_type: type, photo_base64,
        gps_lat: gps.lat, gps_lng: gps.lng, gps_accuracy: gps.acc,
      });
      toast.success(`Clocked ${type === "in" ? "IN" : "OUT"} ✓`);
      if (type === "in") {
        // Jump straight to the dedicated walkie-talkie screen so the CLOCK OUT
        // button can't be tapped by accident during the shift.
        navigate("/walkie", { replace: true });
        return;
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Airplane size={20} weight="duotone" className="text-[#FF4F00]" />
          <div>
            <div className="font-display font-black text-sm leading-none">TOM <span className="text-[#FF4F00]">CARGO</span></div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Punch Card</div>
          </div>
        </div>
        <button onClick={doLogout} data-testid="punch-logout-btn" className="text-xs text-zinc-400 flex items-center gap-1">
          <SignOut size={12} /> {user.name}
        </button>
      </div>

      <div className="px-4 py-3 border-b border-zinc-800 text-center">
        <div className="font-mono text-2xl" data-testid="clock-display">{now.toLocaleTimeString()}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{now.toDateString()}</div>
      </div>

      <div className="px-4 py-3 flex items-center gap-2 text-xs">
        <MapPin size={14} className={gps ? "text-emerald-400" : "text-amber-400"} />
        {gps ? (
          <span className="font-mono text-zinc-300" data-testid="gps-status">
            {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{Math.round(gps.acc)}m
          </span>
        ) : (
          <span className="text-amber-400" data-testid="gps-status">{gpsErr || "Locating…"}</span>
        )}
      </div>

      <div className="relative bg-zinc-900 mx-4 aspect-[3/4] max-h-80 overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${facingMode === "user" ? "scale-x-[-1]" : ""}`} data-testid="camera-preview" />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-mono bg-black/60 px-2 py-1">
          <Camera size={10} /> LIVE · {facingMode === "user" ? "FRONT" : "BACK"}
        </div>
        <button
          onClick={flipCamera}
          disabled={flipping}
          data-testid="flip-camera-btn"
          className="absolute bottom-2 right-2 bg-black/70 hover:bg-black/90 disabled:opacity-40 text-white p-2 rounded-full transition-colors"
          title="Flip camera (front ↔ back)"
        >
          <ArrowsClockwise size={18} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 p-4">
        <button
          onClick={() => punch("in")}
          disabled={busy || !gps}
          data-testid="clock-in-btn"
          className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 py-6 flex flex-col items-center gap-1 transition-colors duration-150"
        >
          <Clock size={28} weight="fill" />
          <span className="font-display font-black text-lg">CLOCK IN</span>
          {busy === "in" && <span className="text-[10px]">saving…</span>}
        </button>
        <button
          onClick={() => punch("out")}
          disabled={busy || !gps}
          data-testid="clock-out-btn"
          className="bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-50 py-6 flex flex-col items-center gap-1 transition-colors duration-150"
        >
          <Clock size={28} weight="regular" />
          <span className="font-display font-black text-lg">CLOCK OUT</span>
          {busy === "out" && <span className="text-[10px]">saving…</span>}
        </button>
      </div>

      {/* If for some reason the auto-redirect after IN didn't fire (network, etc.),
          give the worker a manual way to reach the walkie-talkie screen. */}
      {initialChecked && recent[0] && recent[0].punch_type === "in" && (
        <button
          onClick={() => navigate("/walkie")}
          data-testid="open-walkie-btn"
          className="mx-4 mb-2 border border-[#FF4F00] text-[#FF4F00] hover:bg-[#FF4F00] hover:text-white py-3 flex items-center justify-center gap-2 transition-colors"
        >
          <Radio size={16} weight="fill" />
          <span className="font-display font-black text-sm uppercase">Open Walkie-Talkie</span>
        </button>
      )}

      <div className="px-4 pb-6 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Recent punches</div>
        <div className="space-y-2">
          {recent.length === 0 ? (
            <div className="text-xs text-zinc-500 py-4 text-center">No punches yet.</div>
          ) : recent.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm bg-zinc-900 border border-zinc-800 px-3 py-2" data-testid={`recent-${p.id}`}>
              <div className="flex items-center gap-2">
                {p.punch_type === "in" ? (
                  <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                ) : (
                  <WarningCircle size={16} weight="fill" className="text-red-400" />
                )}
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-300">Clock {p.punch_type}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{new Date(p.timestamp).toLocaleString()}</div>
                </div>
              </div>
              {p.shift_code && (
                <span className={`text-[10px] font-mono border px-1.5 py-0.5 ${p.shift_is_overtime ? "border-amber-500 text-amber-400" : "border-emerald-500 text-emerald-400"}`}>
                  Shift {p.shift_code}{p.shift_is_overtime ? " · OT" : ""}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
